import React, { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Quote, Sparkles, Award, Instagram, Linkedin, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { siteMeta } from "../data/mock";
import AnimatedCounter from "./ui/AnimatedCounter";

// Dramatic AI-style cinematic portrait section for Chitra —
// uses a moody stylized woman image (treated with duotone) instead of a real headshot.
const ChitraSpotlight = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.05, 1, 1.05]);

  // Mouse light spot tracking (local to section)
  const [spot, setSpot] = useState({ x: 50, y: 40 });
  // Global mouse position for eye tracking
  const [globalMouse, setGlobalMouse] = useState({ x: 50, y: 50 });

  useEffect(() => {
    const handleGlobalMove = (e) => {
      setGlobalMouse({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100
      });
    };
    window.addEventListener("mousemove", handleGlobalMove);
    return () => window.removeEventListener("mousemove", handleGlobalMove);
  }, []);

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setSpot({
      x: ((e.clientX - r.left) / r.width) * 100,
      y: ((e.clientY - r.top) / r.height) * 100
    });
  };

  return (
    <section
      id="spotlight"
      ref={ref}
      onMouseMove={onMove}
      className="relative overflow-hidden"
      style={{
        background: "linear-gradient(180deg, #0a0908 0%, #14110f 50%, #0a0908 100%)"
      }}
    >
      {/* Mouse light spot */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(500px 400px at ${spot.x}% ${spot.y}%, rgba(228,199,122,0.08), transparent 60%)`,
          transition: "background 0.2s ease-out"
        }}
      />

      <div className="container-xl py-24 md:py-32">
        <div className="flex items-center gap-4 mb-10">
          <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--gold)]">03</span>
          <span className="w-16 h-px bg-[var(--rule)]" />
          <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--cream-muted)]">
            CHITRA · IN HER OWN WORDS
          </span>
        </div>

        <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-20 items-center">
          {/* AI-styled portrait */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div
              className="relative rounded-sm overflow-hidden"
              style={{
                aspectRatio: "4 / 5",
                boxShadow:
                  "0 40px 80px rgba(0,0,0,0.6), 0 0 60px rgba(228,199,122,0.08)"
              }}
            >
              {/* Base image with duotone + ken burns */}
              <motion.div
                style={{ y, scale }}
                className="absolute inset-0"
              >
                <div
                  className="w-full h-full"
                  style={{
                    backgroundImage:
                      "url(/images/chitra_katya.jpeg)",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    filter:
                      "grayscale(0.4) contrast(1.15) brightness(0.8) sepia(0.15)"
                  }}
                />

                {/* Mouse Tracking Eyes */}
                <Eye x="46.8%" y="40.6%" spot={globalMouse} />
                <Eye x="51.6%" y="40.6%" spot={globalMouse} />

                {/* Gold duotone wash */}
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(184,115,51,0.15) 0%, transparent 35%, rgba(10,9,8,0.7) 100%)",
                    mixBlendMode: "color-burn"
                  }}
                />
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(145deg, rgba(228,199,122,0.12) 0%, transparent 30%, rgba(10,9,8,0.55) 100%)"
                  }}
                />
              </motion.div>

              {/* Corner ticks */}
              <div
                className="absolute top-4 left-4 w-5 h-5"
                style={{
                  borderTop: "1px solid #e4c77a",
                  borderLeft: "1px solid #e4c77a"
                }}
              />
              <div
                className="absolute top-4 right-4 w-5 h-5"
                style={{
                  borderTop: "1px solid #e4c77a",
                  borderRight: "1px solid #e4c77a"
                }}
              />
              <div
                className="absolute bottom-4 left-4 w-5 h-5"
                style={{
                  borderBottom: "1px solid #e4c77a",
                  borderLeft: "1px solid #e4c77a"
                }}
              />
              <div
                className="absolute bottom-4 right-4 w-5 h-5"
                style={{
                  borderBottom: "1px solid #e4c77a",
                  borderRight: "1px solid #e4c77a"
                }}
              />

              {/* Top chip */}
              <div className="absolute top-5 left-1/2 -translate-x-1/2 flex items-center gap-2">
                <span
                  className="font-impact text-[9px] tracking-[0.3em] px-2.5 py-1 rounded-full"
                  style={{
                    background: "rgba(10,9,8,0.6)",
                    border: "1px solid rgba(228,199,122,0.5)",
                    color: "#e4c77a",
                    backdropFilter: "blur(6px)"
                  }}
                >
                  · AI CINEMATIC PORTRAIT
                </span>
              </div>

              {/* Name badge */}
              <div className="absolute bottom-6 left-6 right-6">
                <div className="font-display italic text-[28px] leading-none text-[var(--cream)]">
                  Chitra Katyal
                </div>
                <div className="mt-2 font-impact text-[13px] tracking-[0.3em] text-[var(--gold)] font-medium">
                  BRAND STRATEGIST & STORYTELLER
                </div>
              </div>
            </div>

            {/* Floating sparkle stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="absolute -bottom-6 -right-6 px-5 py-4 rounded-sm hidden md:block"
              style={{
                background: "linear-gradient(135deg, #1c1815, #0a0908)",
                border: "1px solid rgba(228,199,122,0.4)",
                boxShadow: "0 20px 40px rgba(0,0,0,0.4)"
              }}
            >
              <div className="flex items-center gap-2 text-[var(--gold)]">
                <Sparkles size={14} />
                <span className="font-impact text-[12px] tracking-[0.3em] font-medium">ON DESK SINCE</span>
              </div>
              <div className="font-display text-3xl text-[var(--cream)] leading-none mt-2">
                <AnimatedCounter value={14} suffix="+" />
              </div>
              <div className="mt-1 text-[10px] text-[var(--muted)] uppercase tracking-[0.2em]">
                Years of Strategy & Storytelling
              </div>
            </motion.div>
          </motion.div>

          {/* Content */}
          <div className="flex flex-col h-full justify-between">
            <div>
              <Quote className="text-[var(--gold)] opacity-30" size={54} strokeWidth={1} />
              <motion.blockquote
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9 }}
                className="mt-6 font-display italic text-[clamp(28px,3.8vw,56px)] leading-[1.1] text-[var(--cream)] balance"
              >
                “Brands are not built through campaigns alone. <br />They are remembered through stories that feel <span className="gold-text not-italic">human</span>.”
              </motion.blockquote>

              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, delay: 0.2 }}
                className="mt-10 space-y-6 text-[16px] leading-relaxed text-[var(--cream-muted)] max-w-xl font-light"
              >
                <p>
                  <span className="text-[var(--cream)] font-medium">Chitra Katyal</span> is the Founder & Creative Head of Meraki Solutions — a creative studio where branding, storytelling, strategy, and culture intersect.
                </p>
                <p>
                  With a background spanning media, operations, production, design, and digital marketing, she approaches every brand like a living narrative — shaping not just how it looks, but how it feels, speaks, and stays remembered.
                </p>
                <p>
                  From heritage storytelling and visual identity systems to artist management, social media strategy, films, and experiential campaigns, her work blends intuition with structure and aesthetics with business clarity.
                </p>
                <p className="pt-4 border-t border-[rgba(228,199,122,0.1)] text-lg">
                  <span className="text-[var(--gold)] italic">At Meraki, every project begins with one question:</span><br />
                  <span className="text-[var(--cream)] font-medium">What truth does this brand want the world to remember?</span>
                </p>
              </motion.div>

              {/* Highlight row */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, delay: 0.35 }}
                className="mt-12 grid grid-cols-2 gap-8 max-w-lg"
              >
                <Pillar icon={<Award size={18} />} k="Recognitions" v="Boutique Agency of the Year, Central India Brand Awards" />
                <Pillar icon={<Sparkles size={18} />} k="Speaks At" v="SIBM Pune, Delhi University, Bhopal Design Week" />
              </motion.div>
            </div>

            <div className="mt-10">
              {/* Socials & CTA Button Row */}
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, delay: 0.45 }}
                className="pt-8 border-t border-[rgba(201,169,97,0.15)] flex flex-col md:flex-row items-center justify-between gap-8"
              >
                {/* Social Icons (Left Side) */}
                <div className="flex gap-4">
                  <SocialCircle href={siteMeta.socials.linkedin} label="LinkedIn">
                    <Linkedin size={16} />
                  </SocialCircle>
                  <SocialCircle href={siteMeta.socials.instagram} label="Instagram">
                    <Instagram size={16} />
                  </SocialCircle>
                </div>

                {/* Explore Button (Right Side) */}
                <Link
                  to="/chitra"
                  className="btn-primary group"
                  data-cursor="explore"
                >
                  <span className="font-impact tracking-[0.15em]">
                    EXPLORE THE JOURNEY
                  </span>
                  <ArrowRight
                    size={18}
                    className="transition-transform duration-500 group-hover:translate-x-1"
                  />
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Pillar = ({ icon, k, v }) => (
  <div className="flex items-start gap-3">
    <span
      className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center text-[var(--gold)]"
      style={{ border: "1px solid rgba(201,169,97,0.4)" }}
    >
      {icon}
    </span>
    <div>
      <div className="font-impact text-[12px] tracking-[0.3em] text-[var(--gold)] font-medium mb-1">
        {k.toUpperCase()}
      </div>
      <div className="text-[13px] text-[var(--cream)] leading-snug">{v}</div>
    </div>
  </div>
);

const SocialCircle = ({ href, label, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    aria-label={label}
    className="btn-icon"
    data-cursor="follow"
  >
    {children}
  </a>
);



export default ChitraSpotlight;

const Eye = ({ x, y, spot }) => {
  // Calculate relative pupil position
  // spot.x and spot.y are in percentage (0-100)
  // We want to move the pupil slightly towards the spot
  const dx = (spot.x - parseFloat(x)) / 10;
  const dy = (spot.y - parseFloat(y)) / 10;

  return (
    <div
      className="absolute w-2 h-1 rounded-full overflow-hidden flex items-center justify-center opacity-70"
      style={{
        left: x,
        top: y,
        transform: "translate(-50%, -50%)",
        background: "rgba(255,255,255,0.05)",
        boxShadow: "0 0 4px rgba(0,0,0,0.8) inset"
      }}
    >
      <motion.div
        animate={{
          x: dx,
          y: dy
        }}
        transition={{ type: "spring", stiffness: 150, damping: 20 }}
        className="w-1.5 h-1.5 rounded-full bg-[rgba(10,9,8,0.95)]"
        style={{
          boxShadow: "0 0 2px rgba(228,199,122,0.3)"
        }}
      />
    </div>
  );
};
